<script setup lang="ts">
import type { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson'
import type { Map, Popup } from 'mapbox-gl'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import 'mapbox-gl/dist/mapbox-gl.css'

const props = withDefaults(
  defineProps<{
    initialMarker?: MarkerProps
    secondaryMarker?: MarkerProps
    locations?: FeatureCollection<Geometry, GeoJsonProperties>
    showWalkingDirections?: boolean
    zoom?: number
  }>(),
  {
    initialMarker: undefined,
    secondaryMarker: undefined,
    showWalkingDirections: false,
    locations: undefined,
    zoom: 12,
  },
)

// Note: this is probably not reactive if the props change
let initialMarker = props.initialMarker
const secondaryMarker = props.secondaryMarker

const { t } = useI18n({
  useScope: 'local',
})

interface MarkerProps {
  latitude: number
  longitude: number
  element?: HTMLElement
  popupHtml?: string
}

const { primary } = useTailwindColors()

const colorMode = useColorMode()

const selectedFeature = ref()
const selectedFeatureLatLng = ref()
const selectedFeatureName = ref('')
const mapElement = shallowRef<HTMLElement>()
const geocoderElement = shallowRef<HTMLElement>()
const popupElement = shallowRef<HTMLElement>()
const map = shallowRef<Map>()
const popup = shallowRef<Popup>()
const geocoder = shallowRef<any>()

// Disabled for now, we might want to enable this later
const showGeocoderElement = false

// TODO: fix typing. Need to look closer look at the docs https://github.com/mapbox/mapbox-gl-js/issues/13203
let mapboxgl: any // was typeof import('mapbox-gl')

function loadLayers() {
  if (!map.value) {
    return
  }

  // Do nothing if source already added
  if (map.value.getSource('places')) {
    return
  }

  if (props.locations) {
    map.value.addSource('places', {
      type: 'geojson',
      data: props.locations as any,
    })

    // Add a layer showing the places.
    map.value.addLayer({
      id: 'places',
      type: 'circle',
      source: 'places',
      paint: {
        'circle-color': primary.value, // colorMode.value === 'dark' ? someOtherValue.value : primary.value,
        'circle-radius': 6,
        'circle-stroke-width': 2,
        'circle-stroke-color': primary.value,
      },
    })

    map.value.on('click', 'places', (e: any) => {
      selectedFeature.value = e.features[0]
      selectedFeatureLatLng.value = e.lngLat
    })

    // Change the cursor to a pointer when the mouse is over the places layer.
    map.value.on('mouseenter', 'places', () => {
      if (!map.value) {
        return
      }

      map.value.getCanvas().style.cursor = 'pointer'
    })

    // Change it back to a pointer when it leaves.
    map.value.on('mouseleave', 'places', () => {
      if (!map.value) {
        return
      }

      map.value.getCanvas().style.cursor = ''
    })
  }
}

function displayRoute(routeGeometry: any) {
  if (!map.value) return

  const routeSource = map.value.getSource('route')

  if (routeSource) {
    // TypeScript type assertion to GeoJSONSource
    (routeSource as mapboxgl.GeoJSONSource).setData(routeGeometry)
  }
  else {
    map.value.addSource('route', {
      type: 'geojson',
      data: routeGeometry,
    })

    map.value.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#1db7dd',
        'line-width': 5,
        'line-opacity': 0.75,
      },
    })
  }
}

function displayWalkingTime(walkingTimeInSeconds: number) {
  if (!map.value) return
  if (!initialMarker) return

  const minutes = Math.round(walkingTimeInSeconds / 60)

  // Create a popup with the walking time
  new mapboxgl.Popup({ offset: 25 })
    // Set the popup's location to initial marker
    .setLngLat([initialMarker.longitude, initialMarker.latitude])
    .setHTML(
      (initialMarker.popupHtml += `<p class="marker-walking-distance">${t(
        'estimatedWalkingTime',
        {
          time: minutes,
        },
      )}</p>`),
    )
    .addTo(map.value)
}

async function showWalkingDirectionsOnMap() {
  if (!initialMarker || !secondaryMarker) {
    return
  }
  const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/walking/${initialMarker.longitude},${initialMarker.latitude};${secondaryMarker.longitude},${secondaryMarker.latitude}?geometries=geojson&access_token=${mapboxgl.accessToken}`
  fetch(directionsUrl)
    .then(response => response.json())
    .then((data) => {
      const durationInSeconds = data.routes[0].duration
      const walkingTime = durationInSeconds
      // Display this information in your UI
      displayWalkingTime(walkingTime)
      displayRoute(data.routes[0].geometry)
    })
    .catch((error) => {
      console.error('Error fetching walking directions:', error)
    })
}

function showInitialMarker() {
  if (!map.value) return
  if (!initialMarker) return

  // Add an (initial) marker to the map at the specified coordinates
  new mapboxgl.Marker({
    element: initialMarker.element,
  })
    .setPopup(
      initialMarker.popupHtml
        ? new mapboxgl.Popup({ offset: 25 }).setHTML(initialMarker.popupHtml)
        : undefined,
    )
    .setLngLat([initialMarker.longitude, initialMarker.latitude])
    .addTo(map.value)

  // Add the secondary marker, if present
  if (secondaryMarker) {
    showSecondaryMarker()
  }

  if (props.showWalkingDirections) {
    showWalkingDirectionsOnMap()
  }

  console.log(
    `showInitialMarker: initialMarker`,
    initialMarker,
    `secondaryMarker`,
    secondaryMarker,
  )
}

function showSecondaryMarker() {
  if (!map.value) return
  if (!secondaryMarker) return

  new mapboxgl.Marker({
    element: secondaryMarker.element,
  })
    .setPopup(
      secondaryMarker.popupHtml
        ? new mapboxgl.Popup({ offset: 25 }).setHTML(secondaryMarker.popupHtml)
        : undefined,
    )
    .setLngLat([secondaryMarker.longitude, secondaryMarker.latitude])
    .addTo(map.value)
}

// async function addNeighborhoodToMap() {
//   if (!map.value) return

//   // Wait 1 sec
//   await new Promise(resolve => setTimeout(resolve, 1000))
// }

const config = useRuntimeConfig()

if (process.dev && !config.public.mapboxToken) {
  console.warn(
    'NUXT_PUBLIC_MAPBOX_TOKEN environment variable is not defined, mapbox features are disabled',
  )
}

onMounted(() => {
  if (!config.public.mapboxToken) {
    console.error(`No public mapbox token found`)
    return
  }

  Promise.all([
    import('mapbox-gl').then(m => m.default),
    import('@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.min.js').then(
      m => m.default,
    ),
  ]).then(([_mapboxgl, MapboxGeocoder]) => {
    mapboxgl = _mapboxgl
    if (!mapElement.value || !geocoderElement.value) {
      return
    }

    mapboxgl.accessToken = config.public.mapboxToken

    map.value = new mapboxgl.Map({
      container: mapElement.value,
      style:
        colorMode.value === 'dark'
          ? 'mapbox://styles/mapbox/dark-v10'
          : 'mapbox://styles/mapbox/light-v10',
      // Center on the initial marker
      center: initialMarker
        ? [initialMarker.longitude, initialMarker.latitude]
        : undefined,
      zoom: props.zoom,
    })

    showInitialMarker()
    showSecondaryMarker()

    geocoder.value = new MapboxGeocoder({
      accessToken: config.public.mapboxToken,
      mapboxgl,
      marker: true,
    })

    map.value?.on('style.load', () => {
      function loadingStyles() {
        if (!map.value?.isStyleLoaded()) {
          setTimeout(loadingStyles, 1500)
          return
        }

        loadLayers()
      }
      loadingStyles()
    })

    geocoderElement.value.appendChild(geocoder.value.onAdd(map.value))

    // addNeighborhoodToMap()
  })
})

watchEffect(
  () => {
    if (!selectedFeature.value || !popupElement.value || !map.value) {
      return
    }

    const feature = selectedFeature.value
    const { geometry, properties } = feature
    const { name } = properties
    const coordinates = geometry.coordinates.slice()

    // console.log('zooming at: ', properties, coordinates)

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    if (selectedFeatureLatLng.value) {
      while (Math.abs(selectedFeatureLatLng.value.lng - coordinates[0]) > 180) {
        coordinates[0]
          += selectedFeatureLatLng.value.lng > coordinates[0] ? 360 : -360
      }
    }

    map.value.flyTo({
      center: coordinates,
      zoom: 15,
      bearing: 0,
      essential: true, // this animation is considered essential with respect to prefers-reduced-motion
    })

    if (popup.value) {
      popup.value.remove()
    }

    popup.value = new mapboxgl.Popup()
      .on('open', () => {
        selectedFeatureName.value = name
      })
      .on('close', () => {
        selectedFeatureName.value = ''
      })
      .setLngLat(coordinates)
      .setHTML(popupElement.value.innerHTML)
      .addTo(map.value)
  },
  { flush: 'post' },
)

watch(
  () => colorMode.value === 'dark',
  () => {
    if (!map.value) {
      return
    }

    if (colorMode.value === 'dark') {
      map.value.setStyle('mapbox://styles/mapbox/dark-v10')
    }
    else {
      map.value.setStyle('mapbox://styles/mapbox/light-v10')
    }
  },
)
</script>

<template>
  <div class="relative">
    <div
      class="bg-muted-100 dark:bg-muted-900 ltablet:flex-row flex h-full flex-col lg:flex-row"
    >
      <div class="ltablet:h-auto relative h-96 grow lg:h-auto">
        <!-- The actual map element -->
        <div ref="mapElement" class="absolute inset-0 size-full" />
        <div
          ref="geocoderElement"
          class="geocoder absolute inset-x-0 top-6 mx-auto flex items-center justify-center px-6 sm:px-0"
          :class="!showGeocoderElement ? 'hidden' : ''"
        />
        <!-- TODO: is this actually being used at the moment? -->
        <div
          ref="popupElement"
          style="display: none; visibility: hidden"
          class="absolute"
        >
          <slot name="selected-feature" :selected-feature="selectedFeature">
            <SimpleMapMarker
              v-if="selectedFeature"
              :logo="selectedFeature.properties.logo"
              :name="selectedFeature.properties.name"
              :description="selectedFeature.properties.description"
            />
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="pcss">
.mapboxgl-popup .mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip {
  @apply relative top-[-2px] z-[30];
}
.mapboxgl-popup .mapboxgl-popup-anchor-top .mapboxgl-popup-tip {
  @apply relative bottom-[-2px] z-[30];
}

.mapboxgl-popup .mapboxgl-popup-anchor-right .mapboxgl-popup-tip {
  @apply relative start-[-2px] z-[30];
}
.mapboxgl-popup .mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
  @apply relative end-[-2px] z-[30];
}

.mapboxgl-popup-close-button {
  @apply flex h-6 w-6 items-center justify-center;
}

.mapboxgl-popup-content {
  @apply shadow-muted-300/30 dark:shadow-muted-900/40 border-muted-200 dark:border-muted-700 dark:bg-muted-800 rounded-xl border bg-white shadow-xl;
}

.mapboxgl-ctrl-geocoder {
  @apply bg-transparent shadow-none;
}

.mapboxgl-ctrl-geocoder input {
  @apply text-muted-700 dark:text-muted-100 dark:bg-muted-800 border-muted-200 dark:border-muted-700 shadow-muted-300/30 dark:shadow-muted-900/40 h-12 rounded-full border bg-white ps-12 font-sans shadow-xl transition-colors duration-300;
}

.mapboxgl-ctrl-geocoder--button {
  @apply bg-transparent;
}

.mapboxgl-ctrl-geocoder--icon-search {
  @apply text-primary-500 start-3.5 top-3.5 scale-[1.1] fill-current;
}

.mapboxgl-ctrl-geocoder--icon-close {
  @apply text-muted-400 dark:text-muted-100 fill-current;
}
</style>

<i18n lang="json">
{
  "en": {
    "estimatedWalkingTime": "Estimated walking time: {time} minutes"
  },
  "nl": {
    "estimatedWalkingTime": "Geschatte looptijd: {time} minuten"
  }
}
</i18n>
